import { AmountSuperScript } from "components/design/AmountSuperScript";
import { Button } from "components/DesignSystem/Button/Button";
import { InfoItem } from "components/DesignSystem/InfoItem/InfoItem";
import { Link } from "components/DesignSystem/Link/Link";
import Loader from "components/DesignSystem/Loader/loader";
import Slider from "components/DesignSystem/Slider/Slider";
import { Accordion } from "components/DesignSystem/Accordion/Accordion";
import { FilePreviewTag } from "components/FilePreviewTag/FilePreviewTag";
import { DownloadIcon } from "components/icons/Download";
import { PAID } from "constants/billing";
import { useCurrentEntityId } from "hooks/useCurrentEntityId";
import { useCurrentGroupContext } from "hooks/useCurrentGroupContext";
import { useParams } from "react-router-dom";
import { useGetBillingInvoiceQuery } from "store/apis/billingInvoice";
import { BillingInvoice, InvoiceTaskItem } from "types/Models/billing";
import { formatDate } from "utils/formatDate";
import { ConditionalLink } from "components/conditionalLink";

const invoiceTypeMap = {
  RECURRING: "Subscription",
  ONE_TIME: "One time",
  BALANCING_PAYMENT: "Balancing payment",
  AMENDMENT: "Amendment",
  CART: "One time",
  MONTHLY_BOOKKEEPING: "Monthly bookkeeping",
  INCORPORATION: "One time",
};

const MultipleInvoiceContent = ({ invoice }: { invoice: BillingInvoice }) => {
  const taskItems: Record<string, InvoiceTaskItem[]> = invoice!.tasks!.reduce(
    (acc, item) => {
      const key = item?.product_type?.toLowerCase();
      if (!acc[key]) {
        acc[key] = [];
      }
      acc[key].push(item);
      return acc;
    },
    {} as Record<string, InvoiceTaskItem[]>
  );
  return (
    <div className="t-pr-4">
      <div className="t-flex t-justify-between t-items-center t-py-3 t-border-0 t-border-b t-border-solid t-border-neutral-20">
        <span className="t-text-text-30 t-text-subtext-sm">Item</span>
        <span className="t-text-text-30 t-text-subtext-sm">Billing type</span>
      </div>

      {Object.entries(taskItems).map(([key, items], index) => {
        return (
          <>
            <div className="t-my-2">
              <span className="t-text-text-30 t-text-caption t-capitalize">
                {key}
              </span>
            </div>
            {items.map((item, index) => {
              return (
                <div key={index} className="t-flex t-flex-col t-gap-1 ">
                  <div className="t-flex t-items-center t-justify-between">
                    {item.uuid ? (
                      <ConditionalLink
                        to={`/filings/${item.uuid}`}
                        target="__blank"
                        className="t-text-purple  t-text-body-sm hover:t-text-purple hover:!t-underline"
                      >
                        {item.title}
                      </ConditionalLink>
                    ) : (
                      <p className="t-m-0 t-text-text-100 t-text-body-sm">
                        {item.title}
                      </p>
                    )}

                    <span className="t-text-body-sm t-text-text-60">
                      One time
                    </span>
                  </div>
                </div>
              );
            })}
            {!Boolean(index % 2) && (
              <div className="t-h-px t-bg-neutral-10 t-mt-2" />
            )}
          </>
        );
      })}

      <div className="t-mt-6 t-flex t-flex-col t-gap-6">
        <InfoItem className="t-gap-y-0" label="Total amount">
          <AmountSuperScript amount={Number(invoice.amount)} />
        </InfoItem>
        <InfoItem className="t-gap-y-0" label="Status">
          {invoice.status}
        </InfoItem>
      </div>
    </div>
  );
};

const SingleInvoiceContent = ({ invoice }: { invoice: BillingInvoice }) => {
  return (
    <Accordion.ItemGrid>
      <div className="t-col-span-2">
        <InfoItem label="Description">
          {invoice.task ? (
            <Link to={`/tax/filings/${invoice.task.uuid}`}>
              {invoice.task.title}
            </Link>
          ) : (
            invoice.short_name
          )}
        </InfoItem>
      </div>

      {invoice.paid_on && (
        <div className="t-col-span-2">
          <InfoItem label="Paid on">{formatDate(invoice.paid_on)}</InfoItem>
        </div>
      )}

      {invoice.invoice_type && (
        <div className="t-col-span-2">
          <InfoItem label="Billing type">
            {invoiceTypeMap[invoice.invoice_type]}
          </InfoItem>
        </div>
      )}

      {invoice.payment_card_details && (
        <div className="t-col-span-2">
          <InfoItem label="Payment method">
            <Link to={`/settings/billing/paymentmethod?entity=`}>
              <span className="t-capitalize">
                {invoice.payment_card_details.brand}
              </span>{" "}
              ****
              {invoice.payment_card_details.last_four}
            </Link>
          </InfoItem>
        </div>
      )}

      {invoice.status && (
        <div className="t-col-span-2">
          <InfoItem label="Status">{invoice.status}</InfoItem>
        </div>
      )}

      {Number(invoice.credits_used) > 0 && (
        <div className="t-col-span-2">
          <InfoItem label="Credits used">{invoice.credits_used}</InfoItem>
        </div>
      )}
      {Number(invoice.late_fee) > 0 && (
        <div className="t-col-span-2 t-text-red">
          <InfoItem label="Late fee">$ {invoice.late_fee}</InfoItem>
        </div>
      )}
      {Number(invoice.amount) > 0 && (
        <div className="t-col-span-2">
          <InfoItem label="Total amount">$ {invoice.amount}</InfoItem>
        </div>
      )}
    </Accordion.ItemGrid>
  );
};

export const BillingSlider = ({
  openCheckoutModal,
  entityIdFromParent,
  invoiceIdFromParent,
  groupId,
}: {
  openCheckoutModal: (invoice: BillingInvoice) => void;
  entityIdFromParent?: string;
  invoiceIdFromParent?: string;
  groupId?: string;
}) => {
  const { invoiceId } = useParams<{ invoiceId: string }>();
  const entityIdFromUrl = useCurrentEntityId();
  const entityId = entityIdFromParent || entityIdFromUrl;
  const invoice_id = invoiceIdFromParent || invoiceId;
  const group = useCurrentGroupContext();

  const { data: invoice } = useGetBillingInvoiceQuery(
    {
      invoiceId: invoice_id,
      groupId: groupId || group.uuid,
      entityId: entityId!,
    },
    { skip: !invoice_id || !entityId || (!group.uuid && !groupId) }
  );

  if (invoice) {
    return (
      <>
        <Slider.Header>
          <Slider.Title>{invoice.name}</Slider.Title>
          <Slider.Close />
        </Slider.Header>
        <Slider.Body>
          <Accordion.Root
            type="multiple"
            defaultValue={["ABOUT", "DOCUMENTS"]}
            className="t-space-y-5"
          >
            <Accordion.Item value="ABOUT">
              <Accordion.Trigger>About</Accordion.Trigger>
              <Accordion.Content>
                {Boolean(invoice.tasks && invoice.tasks.length > 1) ? (
                  <MultipleInvoiceContent invoice={invoice} />
                ) : (
                  <SingleInvoiceContent invoice={invoice} />
                )}
              </Accordion.Content>
            </Accordion.Item>

            <Accordion.Item value="DOCUMENTS">
              <Accordion.Trigger>Documents</Accordion.Trigger>
              <Accordion.Content>
                <Accordion.ItemGrid>
                  {invoice.document && (
                    <div className="t-col-span-2">
                      <InfoItem label="Invoice">
                        <FilePreviewTag block file={invoice.document}>
                          <DownloadIcon color="currentColor" />
                        </FilePreviewTag>
                      </InfoItem>
                    </div>
                  )}

                  {invoice.receipt_document && (
                    <div className="t-col-span-2">
                      <InfoItem label="Receipt">
                        <FilePreviewTag block file={invoice.receipt_document}>
                          <DownloadIcon color="currentColor" />
                        </FilePreviewTag>
                      </InfoItem>
                    </div>
                  )}
                </Accordion.ItemGrid>
              </Accordion.Content>
            </Accordion.Item>
          </Accordion.Root>
        </Slider.Body>
        {invoice.status !== PAID && !isNaN(Number(invoice.amount)) && (
          <Slider.FooterButtonGroup>
            <Button
              customType="primary"
              block
              onClick={openCheckoutModal.bind(this, invoice)}
            >
              Pay &nbsp; <AmountSuperScript amount={Number(invoice.amount)} />{" "}
            </Button>
          </Slider.FooterButtonGroup>
        )}
      </>
    );
  }

  return (
    <Slider.Body className="t-h-full">
      <div className="t-h-full t-flex t-w-full t-justify-center t-items-center">
        <Loader size="small" />
      </div>
    </Slider.Body>
  );
};
